/**
 * Shared utilities for {@link EventSummaryRow}, {@link EventSummaryCard} and {@link EventSummariesDataTable}.
 */
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import type {
  EventSummary,
  EventSummaryFilters,
  ListEventSummariesParams,
} from '@/types/event-summary.types';
import { resolveListDateRange } from '@/utils/list-date-range-utils';

/** Formats the event date attached to a summary for display (e.g. "3 jan. 2026"). */
export function formatSummaryEventDate(summary: EventSummary): string {
  return summary.event?.eventDate
    ? format(new Date(summary.event.eventDate), 'd MMM yyyy', { locale: fr })
    : 'Date inconnue';
}

/** Resolves the activity name from the summary's linked event. */
export function deriveSummaryActivityName(summary: EventSummary): string {
  return summary.event?.activity?.label || 'Activité inconnue';
}

/** Formats the filledAt date for display (e.g. "3 jan. 2026"), or "—". */
export function formatSummaryFilledDate(filledAt: Date | string | null | undefined): string {
  return filledAt ? format(new Date(filledAt as string), 'd MMM yyyy', { locale: fr }) : '—';
}

/**
 * Map chips / custom dates to the list API `dateFrom`/`dateTo`.
 * Custom range wins; `all` sends neither bound; week is Monday–Sunday.
 */
export function resolveDateRange(filters: EventSummaryFilters): { dateFrom?: Date; dateTo?: Date } {
  if (filters.customDateFrom || filters.customDateTo) {
    return {
      dateFrom: filters.customDateFrom,
      dateTo: filters.customDateTo,
    };
  }
  if (filters.dateRange === 'all') return {};
  if (filters.dateRange === 'year') {
    const now = new Date();
    return {
      dateFrom: new Date(now.getFullYear(), 0, 1),
      dateTo: new Date(now.getFullYear(), 11, 31),
    };
  }
  return resolveListDateRange(filters, { periodEnd: 'endOfPeriod' });
}

/** List GET params; empty `statuses` / `establishmentIds` become `undefined`. */
export function buildListParams(
  filters: EventSummaryFilters,
  options: {
    page: number;
    pageSize: number;
    eventId?: string;
    dateFrom?: Date;
    dateTo?: Date;
  },
): ListEventSummariesParams {
  return {
    page: options.page,
    pageSize: options.pageSize,
    eventId: options.eventId,
    statuses: filters.statuses.length > 0 ? filters.statuses : undefined,
    establishmentIds:
      filters.establishmentIds && filters.establishmentIds.length > 0
        ? filters.establishmentIds
        : undefined,
    dateFrom: options.dateFrom,
    dateTo: options.dateTo,
    search: undefined,
  };
}

/** Deep-compare against defaults to drive the filter badge. */
export function hasEventSummaryActiveFilters(
  filters: EventSummaryFilters,
  defaults: EventSummaryFilters,
): boolean {
  return JSON.stringify(filters) !== JSON.stringify(defaults);
}
